import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import UserAdmin from './pages/admin/UserAdmin';
import CssBaseline from '@mui/material/CssBaseline';
import { MandateProvider } from './contexts/MandateContext';
import { SecteurProvider } from './contexts/SecteurContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import frLocale from 'date-fns/locale/fr';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import RolesAdminPage from './pages/admin/RolesAdmin';
import { Auth0Provider } from '@auth0/auth0-react';
import { auth0Config } from './auth0-config';

// Components
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Login from './pages/admin/Login';

import MandateManagement from './pages/admin/MandatsAdmin';
import TimeEntry from './pages/TimeEntry';
import ReleveCodeManagement from './pages/admin/CodesAdmin';
import SectorManagement from './pages/admin/SecteursAdmin';
import ActivitiesAdminPage from './pages/admin/ActivitesAdmin';
import PointageMensuel from './pages/PointageMensuel';
import MonthlyActivities from './pages/MonthlyActivities';
import ResetPasswordPage from './pages/admin/ResetPasswordPage';
import SectorCalendar from './pages/SectorCalendar';

const ProtectedRoute = ({ element, permission }) => {
  const { user, roles } = useAuth();
  
  // Permettre l'accès si l'utilisateur est connecté et que le rôle est requis
  if (!user || (permission && (!roles || !roles[permission]))) {
    return <Navigate to="/login" replace />;
  }

  return element;
};

const AppContent = () => {
  const { darkMode } = useTheme();

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
      background: {
        default: darkMode ? '#303030' : '#fff',
        paper: darkMode ? '#424242' : '#fff',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  return (
    <SnackbarProvider maxSnack={3}>
      <SecteurProvider>
        <MandateProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<Navigate to="/login" />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                
                {/* Wrapper Layout protégé pour toutes les autres routes */}
                <Route element={<ProtectedRoute element={<Layout />} />}>
                  {/* Route par défaut -> Pointage Mensuel */}
                  <Route path="/" element={<ProtectedRoute element={<PointageMensuel />} permission="can_access_pointage_mensuel" />} />
                  <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} permission="can_access_dashboard" />} />
                  <Route path="time-entry" element={<ProtectedRoute element={<TimeEntry />} permission="can_access_time_entry" />} />
                  <Route path="mandate-management" element={<ProtectedRoute element={<MandateManagement />} permission="can_access_mandate_management" />} />
                  <Route path="releve-code-management" element={<ProtectedRoute element={<ReleveCodeManagement />} permission="can_access_releve_code_management" />} />
                  <Route path="sector-management" element={<ProtectedRoute element={<SectorManagement />} permission="can_access_sector_management" />} />
                  <Route path="admin/activities" element={<ProtectedRoute element={<ActivityProvider><ActivitiesAdminPage /></ActivityProvider>} permission="can_access_activities_admin" />} />
                  <Route path="pointage-mensuel" element={<ProtectedRoute element={<PointageMensuel />} permission="can_access_pointage_mensuel" />} />
                  <Route path="monthly-activities" element={<ProtectedRoute element={<MonthlyActivities />} permission="can_access_monthly_activities" />} />
                  <Route path="admin/users" element={<ProtectedRoute element={<UserAdmin />} permission="can_access_user_admin" />} />
                  <Route path="admin/roles" element={<ProtectedRoute element={<RolesAdminPage />} permission="can_access_roles_admin" />} />
                  {/* Nouvelle route pour le calendrier secteur */}
                  <Route path="/sector-calendar" element={<ProtectedRoute element={<SectorCalendar />} permission="can_access_sector_calendar" />} />
                </Route>
              </Routes>
            </LocalizationProvider>
          </ThemeProvider>
        </MandateProvider>
      </SecteurProvider>
    </SnackbarProvider>
  );
};

function App() {
  return (
    <Auth0Provider
      {...auth0Config}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      useRefreshTokensFallback={true} // Ajout ici
      onRedirectCallback={(appState) => {
        window.history.replaceState(
          {},
          document.title,
          appState?.returnTo || window.location.pathname
        );
      }}
    >
      <CustomThemeProvider>
        <Router>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
      </CustomThemeProvider>
    </Auth0Provider>
  );
}

export default App;
